import { EventPoster } from '@/features/events/shared/EventPoster';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { useEventDetail } from '@/features/events/useEventDetail';
import { type DiscoveryEvent as RadiousEvent } from '@/lib/api/apiTypes';
import { formatEventDate, formatEventTimeRange } from '@/features/events/eventFormatting';
import { getEventHref } from '@/lib/navigation/routes';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { CalendarDaysIcon, MapPinIcon, XIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

export default function MapPreviewScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const eventDetailQuery = useEventDetail(id);
  const event = eventDetailQuery.data?.event;

  if (!event) {
    return null;
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <SafeAreaView className="flex-1 bg-black/45">
        <View className="flex-1 justify-end">
          <Pressable className="flex-1" onPress={() => router.back()} />

          <View className="overflow-hidden rounded-t-[34px] border-t border-white/10 bg-[#111111] px-4 pb-5 pt-3">
            <View className="mb-4 items-center">
              <View className="h-1.5 w-12 rounded-full bg-white/15" />
            </View>

            <View className="flex-row items-start gap-4">
              <EventPoster event={event as unknown as RadiousEvent} className="h-[116px] w-[88px] rounded-[18px]" showMeta={false} />

              <View className="flex-1 gap-3 pt-1">
                <View className="flex-row items-start justify-between gap-3">
                  <View className="flex-1 gap-1">
                    <Text className="text-[20px] font-semibold leading-[24px] text-[#f2f2f2]">
                      {event.title}
                    </Text>
                    <Text className="text-sm font-medium text-[#f2f2f2]/78">
                      {formatEventDate(event.starts_at)}
                    </Text>
                  </View>

                  <Pressable
                  onPress={() => router.back()}
                  className="rounded-full border border-white/10 bg-white/5 p-2.5">
                    <Icon as={XIcon} size={14} className="text-[#f2f2f2]/82" />
                  </Pressable>
                </View>

                <MetaPill icon={MapPinIcon} label={event.venue_name} />
                <MetaPill icon={CalendarDaysIcon} label={formatEventTimeRange(event.starts_at, event.ends_at)} />

                <Text className="text-sm leading-5 text-[#f2f2f2]/82" numberOfLines={3}>
                  {event.summary}
                </Text>
              </View>
            </View>

            <View className="mt-5 flex-row gap-3">
              <Pressable
                className="h-12 flex-1 items-center justify-center rounded-[18px] bg-[#ef4e2e]"
                onPress={() => {
                  router.back();
                  requestAnimationFrame(() => router.navigate(getEventHref(event.id, event.title) as never));
                }}>
                <Text className="text-sm font-semibold text-[#f2f2f2]">Open event</Text>
              </Pressable>

              <Pressable
                className="h-12 rounded-[18px] border border-white/10 bg-white/5 px-5"
                onPress={() => router.back()}>
                <View className="h-full items-center justify-center">
                  <Text className="text-sm font-semibold text-[#f2f2f2]">Close</Text>
                </View>
              </Pressable>
            </View>
          </View>
        </View>
      </SafeAreaView>
    </>
  );
}

function MetaPill({
  icon,
  label,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
}) {
  return (
    <View className="flex-row items-center gap-2">
      <Icon as={icon} size={14} className="text-[#f2f2f2]/72" />
      <Text className="text-sm text-[#f2f2f2]/84">{label}</Text>
    </View>
  );
}
